import { useEffect, useRef } from "react";
import { FiX } from "react-icons/fi";

interface ModalShellProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title displayed in header */
  title: string;
  /** Content for the left panel (or main content in single column mode) */
  leftPanel: React.ReactNode;
  /** Content for the right panel (ignored in single column mode) */
  rightPanel?: React.ReactNode;
  /** Optional footer content (typically buttons) */
  footer?: React.ReactNode;
  /** Optional error message to display */
  error?: string | null;
  /** Optional className for the modal container */
  className?: string;
  /** Use single column layout instead of two-panel */
  singleColumn?: boolean;
}

/**
 * ModalShell - Reusable two-column modal layout
 *
 * Used by MediaUploadModal and MediaEditModal for consistent layout.
 * Handles escape key, click-outside, and scroll locking.
 */
export function ModalShell({
  isOpen,
  onClose,
  title,
  leftPanel,
  rightPanel,
  footer,
  error,
  className = "",
  singleColumn = false,
}: ModalShellProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed !mt-0 inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={`flex h-[90vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-xl ${className}`}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-4">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close modal"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        {singleColumn ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-6">{leftPanel}</div>
        ) : (
          <div className="flex min-h-0 flex-1">
            {/* Left Panel */}
            <div className="w-1/2 overflow-y-auto border-r p-6">{leftPanel}</div>

            {/* Right Panel */}
            <div className="w-1/2 overflow-y-auto p-6">{rightPanel}</div>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="shrink-0 border-t bg-red-50 px-6 py-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Footer */}
        {footer && (
          <div className="flex shrink-0 justify-end gap-3 border-t px-6 py-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

export default ModalShell;
